import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { ageFromBirth } from "../../lib/calc";
import { Baby, Loader2, Sparkles, Footprints, Hand, MessageCircle, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AiDisclaimer } from "../AiDisclaimer";

const AREA_ICON = {
  "Motor grueso": Footprints, "Motor fino": Hand, "Lenguaje": MessageCircle, "Social": Users,
};

export default function MilestonesPanel({ patient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const ageM = ageFromBirth(patient.birth_date).months;

  const load = useCallback(async () => {
    const ck = `clinped_ms_${ageM}`;
    const cached = localStorage.getItem(ck);
    if (cached) { try { setData(JSON.parse(cached)); return; } catch { /* ignore */ } }
    setLoading(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "milestones", age_months: ageM });
      setData(res.data.parsed);
      if (res.data.parsed) localStorage.setItem(ck, JSON.stringify(res.data.parsed));
      else toast.error("La IA no devolvió hitos");
    } catch (e) { toast.error("Error de IA"); }
    finally { setLoading(false); }
  }, [patient.patient_id, ageM]);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-6 border border-stone-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-sand/30 flex items-center justify-center text-amber-700"><Baby size={24} /></div>
          <div>
            <h3 className="font-outfit font-bold text-brand-ink">Hitos del desarrollo</h3>
            <p className="text-sm text-muted-foreground">Según la edad: {ageFromBirth(patient.birth_date).text} ({ageM} meses)</p>
          </div>
        </div>
        <button data-testid="load-milestones-btn" onClick={load} disabled={loading}
          className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02] disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generar hitos con IA
        </button>
      </div>

      {data && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="milestones-result">
          {data.hitos?.map((h, i) => {
            const Icon = AREA_ICON[h.area] || Sparkles;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-3xl p-5 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-brand-sage/15 flex items-center justify-center text-brand-sageDark"><Icon size={18} /></div>
                  <p className="font-outfit font-bold text-brand-ink">{h.area}</p>
                </div>
                <ul className="space-y-1.5">
                  {h.items?.map((it, j) => (
                    <li key={j} className="text-sm text-brand-ink/80 flex gap-2"><span className="text-brand-sage mt-1">•</span>{it}</li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
          {data.señales_alerta?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="sm:col-span-2 bg-destructive/5 rounded-3xl p-5 border border-destructive/10">
              <p className="font-outfit font-bold text-destructive flex items-center gap-2 mb-2"><AlertTriangle size={18} /> Señales de alerta</p>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {data.señales_alerta.map((s, i) => <li key={i} className="text-sm text-brand-ink/80 flex gap-2"><span className="text-destructive mt-1">•</span>{s}</li>)}
              </ul>
            </motion.div>
          )}
          <AiDisclaimer className="sm:col-span-2" />
        </div>
      )}
    </div>
  );
}
